import { useState, useRef, useEffect } from "react";
import {
  FieldErrors,
  UseFormRegister,
  useController,
  Control,
} from "react-hook-form";
import { format, isValid, parse, parseISO } from "date-fns";
import { ko, enUS } from "date-fns/locale";
import { DayPicker } from "react-day-picker";
import classNames from "classnames";
import ErrorMessage from "../Input/ErrorMessage";
import CalendarIcon from "src/assets/images/icon/calendar_icon.svg";
import DropdownIcon from "src/assets/images/icon/dropdown_icon.svg";
import CalendarLeftIcon from "src/assets/images/icon/calendar_left_icon.svg";
import CalendarRightIcon from "src/assets/images/icon/calendar_right_icon.svg";
import "./index.css";
import "react-day-picker/style.css";
import { useRouter } from "src/hooks/useRouter";

interface IFormDateProps {
  label?: string;
  name: string;
  register: UseFormRegister<any>;
  control?: Control<any>; // control 추가
  errors?: FieldErrors<any>;
  placeholder?: string;
  autoFocus?: boolean;
  required?: boolean;
  children?: React.ReactNode;
  width?: number | string;
  success?: {
    isSuccess: boolean;
    message: string;
  };
  disabled?: boolean;
  dateFormat?: string;
  defaultValue?: string | Date;
}

const FormDate = ({
  errors,
  name,
  register,
  control, // control 추가
  label,
  placeholder = "YYYY-MM-DD",
  autoFocus,
  children,
  width,
  required = false,
  success,
  disabled,
  dateFormat = "yyyy-MM-dd",
  defaultValue,
}: IFormDateProps) => {
  const { lang } = useRouter();
  const isKorean = lang === "ko";
  // 기본값 포맷팅
  const getFormattedDefaultValue = () => {
    if (!defaultValue) return "";

    if (defaultValue instanceof Date) {
      return format(defaultValue, dateFormat);
    }

    const isoParsed = parseISO(defaultValue as string);
    if (isValid(isoParsed)) {
      return format(isoParsed, dateFormat);
    }

    return defaultValue as string;
  };

  // useController를 사용하여 react-hook-form과 완전히 동기화
  const {
    field: { onChange: fieldOnChange, onBlur, value: fieldValue, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: getFormattedDefaultValue(),
    rules: { required },
  });

  // 현재 폼 값을 기준으로 Date 객체 생성
  const getCurrentDate = (value: string) => {
    if (!value) return undefined;

    const isoParsed = parseISO(value);
    if (isValid(isoParsed)) {
      return isoParsed;
    }

    const parsedDate = parse(value, dateFormat, new Date());
    return isValid(parsedDate) ? parsedDate : undefined;
  };

  const currentDate = getCurrentDate(fieldValue);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [month, setMonth] = useState(currentDate || new Date());

  const calendarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSuccess = success && success.isSuccess;

  // fieldValue가 변경될 때마다 month 업데이트
  useEffect(() => {
    const date = getCurrentDate(fieldValue);
    if (date) {
      setMonth(date);
    }
  }, [fieldValue, dateFormat]);

  // 캘린더 외부 클릭시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputClick = () => {
    if (!disabled) {
      setIsCalendarOpen(!isCalendarOpen);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // 날짜 형식으로 파싱 시도
    const parsedDate = parse(value, dateFormat, new Date());
    if (isValid(parsedDate)) {
      setMonth(parsedDate);
    }

    // react-hook-form 값 업데이트
    fieldOnChange(value);
  };

  const handleDayPickerSelect = (date: Date | undefined) => {
    if (!date) {
      return;
    }

    setMonth(date);
    const formattedDate = format(date, dateFormat);

    // react-hook-form 값 업데이트
    fieldOnChange(formattedDate);
    setIsCalendarOpen(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsCalendarOpen(false);
    }
  };

  return (
    <div className="form-date-field">
      {label && (
        <label htmlFor={name} className="form-date-label">
          {label} {required && <span className="required">*</span>}
        </label>
      )}
      <div className="form-date-wrapper">
        <div className="form-date-icon-container">
          <img src={CalendarIcon} alt="달력" className="form-date-icon" />
        </div>
        <input
          ref={(e) => {
            ref(e);
            if (inputRef.current !== e) {
              (inputRef as any).current = e;
            }
          }}
          type="text"
          id={name}
          style={{ width }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          className={classNames(
            "form-date-input",
            !!error && "invalid",
            isCalendarOpen && "focused"
          )}
          value={fieldValue || ""} // fieldValue 사용
          onChange={handleInputChange}
          onBlur={onBlur}
          onClick={handleInputClick}
          onKeyDown={handleInputKeyDown}
          name={name}
          readOnly
        />
        <span className="form-date-children">{children}</span>

        {isCalendarOpen && (
          <div ref={calendarRef} className="form-date-calendar">
            <DayPicker
              mode="single"
              selected={currentDate} // currentDate 사용
              onSelect={handleDayPickerSelect}
              month={month}
              onMonthChange={setMonth}
              locale={isKorean ? ko : enUS}
              weekStartsOn={0}
              showOutsideDays
              captionLayout="dropdown"
              startMonth={new Date(2020, 0)} // 2020년 1월부터
              endMonth={new Date(2030, 11)} // 2030년 12월까지
              className="custom-day-picker"
              formatters={{
                formatCaption: (date: Date) =>
                  format(date, "yyyy년 M월", { locale: isKorean ? ko : enUS }),
                formatWeekdayName: (date: Date) =>
                  format(date, "eee", { locale: isKorean ? ko : enUS }),
              }}
              components={{
                Chevron: ({ orientation }) => (
                  <>
                    {orientation === "down" ? (
                      <img
                        style={{
                          width: "9.6px",
                          height: "5.6px",
                          marginLeft: "9.2px",
                          pointerEvents: "none",
                        }}
                        src={DropdownIcon}
                        alt="dropdown"
                      />
                    ) : orientation === "left" ? (
                      <img
                        style={{
                          width: "5.61px",
                          height: "9.59px",
                          pointerEvents: "none",
                        }}
                        src={CalendarLeftIcon}
                        alt="calendar-left"
                      />
                    ) : orientation === "right" ? (
                      <img
                        style={{
                          width: "5.61px",
                          height: "9.59px",
                          pointerEvents: "none",
                        }}
                        src={CalendarRightIcon}
                        alt="calendar-right"
                      />
                    ) : (
                      <></>
                    )}
                  </>
                ),
              }}
            />
          </div>
        )}
      </div>
      {error && (
        <ErrorMessage type="error" message={error?.message as string} />
      )}
      {isSuccess && <ErrorMessage type="success" message={success.message} />}
    </div>
  );
};

export default FormDate;
